'use client';

// A-0008.03: Classic + Beta creation paths removed. Only Graphical (V2) and
// JSON modes remain — both create the same V2+ opcode shape.
import {
 Dialog, DialogContent, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
 Button
} from 'shared/components/ui/button';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Sparkles, Zap, ArrowRight
} from 'lucide-react';

interface BetaFeatureModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    handleUseJson: () => void
    handleUseGraphicalV2: () => void
}

export function BetaFeatureModal({
    open,
    handleUseJson,
    handleUseGraphicalV2,
    onOpenChange
}: BetaFeatureModalProps) {

    const pick = (fn: () => void) => {
        onOpenChange(false);
        fn();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden border-0 bg-gradient-to-br from-background via-background to-accent/5">
                <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/10" />
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-full blur-2xl" />

                    <div className="relative p-8">
                        <DialogHeader className="text-center mb-6">
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <div className="p-2 rounded-full bg-primary/10">
                                    <Sparkles className="w-6 h-6 text-primary" />
                                </div>
                                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                                    Create Opcode
                                </Badge>
                            </div>
                            <DialogTitle className="text-2xl font-bold text-balance">
                                Choose how to author your opcode
                            </DialogTitle>
                            <p className="text-muted-foreground text-pretty mt-2">
                                Both modes create the same V2+ opcode. You can switch between Graphical and JSON editing at any time.
                            </p>
                        </DialogHeader>

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                            {/* Graphical V2 Option */}
                            <div className="relative group">
                                <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="relative p-6 border border-green-500/20 rounded-lg bg-card/50 backdrop-blur-sm hover:border-green-500/40 transition-colors h-full flex flex-col">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 rounded-full bg-green-500/10">
                                            <Zap className="w-5 h-5 text-green-500" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold">Graphical Builder</h3>
                                            <Badge variant="outline" className="text-xs border-green-500/30 text-green-500">
                                                Recommended
                                            </Badge>
                                        </div>
                                    </div>
                                    <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                                        <li className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                            Visual step builder
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                            All step types
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                            Inline validation
                                        </li>
                                    </ul>
                                    <Button onClick={() => pick(handleUseGraphicalV2)} className="w-full bg-green-600 hover:bg-green-700 mt-auto">
                                        Use Graphical
                                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </div>
                            </div>

                            {/* JSON Option */}
                            <div className="p-6 border border-border rounded-lg bg-card/30 flex flex-col">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-full bg-accent/20">
                                        <div className="w-5 h-5 rounded bg-accent/40" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold">JSON Editor</h3>
                                        <p className="text-xs text-muted-foreground">Direct JSON editing</p>
                                    </div>
                                </div>
                                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                                    <li className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                                        JSON-first editor
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                                        Step templates
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                                        Schema validation
                                    </li>
                                </ul>
                                <Button variant="outline" onClick={() => pick(handleUseJson)} className="w-full bg-transparent mt-auto">
                                    Use JSON Editor
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
